import { useMemo, useState } from "react";
import { PILLARS, PILLAR_COLORS, num, type PillarRank, type PillarRow } from "../../lib/pillars";

const SHOWN = 300;
type Mode = "ranking" | "movers";

/**
 * The ranked table, and the movers within it.
 *
 * A "mover" compares this month-end with the previous one under the SAME weights and
 * filters, so a name moves only because its scores did. Comparing across two
 * weightings would show the sliders, not the companies.
 */
export function RankingsView({
  data,
  selected,
  onToggle,
}: {
  data: PillarRank;
  selected: number[];
  onToggle: (cik: number) => void;
}) {
  const [mode, setMode] = useState<Mode>("ranking");

  const rows = useMemo(() => {
    if (mode === "ranking") return data.rows.slice(0, SHOWN);
    return data.rows
      .filter((r) => r.prev_rank != null)
      .sort((a, b) => Math.abs(delta(b)) - Math.abs(delta(a)))
      .slice(0, 60);
  }, [data, mode]);

  return (
    <div>
      <div className="sticky top-0 z-10 flex items-center gap-2 px-3 py-1.5 bg-term-bg border-b border-term-edge text-[11px]">
        {(["ranking", "movers"] as Mode[]).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={mode === m ? "text-term-accent" : "text-term-dim hover:text-term-text"}
          >
            {m === "ranking" ? `Top ${Math.min(SHOWN, data.n_ranked)}` : "Biggest movers"}
          </button>
        ))}
        <span className="text-term-dim">
          {data.prev_on ? `Δ vs ${data.prev_on}` : "no earlier month to compare"}
          {data.index_as_of ? ` · index members as of ${data.index_as_of}` : ""}
        </span>
        <div className="flex-1" />
        <span className="text-term-dim">click rows to chart them ({selected.length}/8)</span>
      </div>
      <table className="w-full text-[12px]">
        <thead className="text-[10px] uppercase tracking-wide text-term-dim">
          <tr className="border-b border-term-edge">
            <th className="text-right px-2 py-1 w-12">#</th>
            <th className="text-right px-2 w-14">Δ</th>
            <th className="text-left px-2 w-20">Ticker</th>
            <th className="text-left px-2">Name</th>
            <th className="text-left px-2 w-16">Sector</th>
            <th className="text-right px-2 w-16">Score</th>
            {PILLARS.map((p) => (
              <th key={p} className="text-left px-2 w-24" style={{ color: PILLAR_COLORS[p] }}>
                {p}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.cik}
              onClick={() => onToggle(r.cik)}
              className={`border-b border-term-edge/50 cursor-pointer hover:bg-term-panel ${
                selected.includes(r.cik) ? "bg-term-accent/10" : ""
              }`}
            >
              <td className="text-right px-2 py-1 tnum text-term-dim">{r.rank}</td>
              <td className="text-right px-2 tnum">
                <Delta row={r} />
              </td>
              <td className="px-2 text-term-accent">{r.ticker}</td>
              <td className="px-2 truncate max-w-0">{r.title}</td>
              <td className="px-2 text-term-dim">{r.ff12}</td>
              <td className="text-right px-2 tnum">{num(r.score, 3)}</td>
              {PILLARS.map((p) => (
                <td key={p} className="px-2">
                  <ScoreBar value={r[p]} color={PILLAR_COLORS[p]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const delta = (r: PillarRow): number => (r.prev_rank == null ? 0 : r.prev_rank - r.rank);

function Delta({ row }: { row: PillarRow }) {
  if (row.prev_rank == null) return <span className="text-term-dim">new</span>;
  const d = delta(row);
  if (d === 0) return <span className="text-term-dim">·</span>;
  return <span className={d > 0 ? "text-term-up" : "text-term-down"}>{d > 0 ? `↑${d}` : `↓${-d}`}</span>;
}

/** A pillar score is a percentile, so a bar from 0 to 1 IS the number. Missing is
 *  drawn as missing — a dash, never an empty bar that reads as zero. */
function ScoreBar({ value, color }: { value: number | null; color: string }) {
  if (value == null) return <span className="text-term-dim">—</span>;
  return (
    <div className="flex items-center gap-1.5" title={value.toFixed(3)}>
      <div className="h-1.5 flex-1 bg-term-edge rounded-sm overflow-hidden">
        <div className="h-full" style={{ width: `${value * 100}%`, background: color }} />
      </div>
      <span className="tnum text-[10px] text-term-dim w-6 text-right">{Math.round(value * 100)}</span>
    </div>
  );
}
